import { DayOfWeek, defaultPaymentScheduleRow, InvoiceEveryUnit, NthWeek, PaymentSchedule, PaymentScheduleOptions,  PaymentScheduleQuotedAndTimeAndMaterialsOption,  PaymentScheduleRow, RecurringPayment, RecurringPaymentMonthlyOption } from "../../domain/proposals/settings/proposalSettings"
import { roundToPrecision } from "../../helpers/math"

export type PaymentScheduleAction = 
| { type: 'addPayment', index: number }
| { type: 'deletePayment', index: number }
| { type: 'updatePaymentNote', index: number, value: string }
| { type: 'updatePaymentTitle', index: number, value: string }
| { type: 'updatePaymentPercentage', index: number, value: string }

export const toPaymentScheduleRows = (paymentSchedule: PaymentSchedule[]): PaymentScheduleRow[] => {
    return paymentSchedule.map((payment, index) => {
      const isFirst = index === 0
      const isLast = index === paymentSchedule.length - 1
      return ({
        ...payment,
        canDelete: !isFirst && !isLast,
        canUpdatePercentage: !isLast
      })
    })
}

export const addPaymentRow = (original: PaymentScheduleRow[], index: number): PaymentScheduleRow[] => {
  let newPaymentSchedule = [...original]
  const newRow: PaymentScheduleRow = {
    amount: 0,
    canDelete: true,
    canUpdatePercentage: true,
    note: '',
    percentage: 0,
    sequence: 0,
    title: ''
  }
  newPaymentSchedule.splice(index + 1, 0, newRow)
  newPaymentSchedule = newPaymentSchedule.map((x, i) => ({ ...x, sequence: i }))
  return newPaymentSchedule
}

export const deletePaymentRow = (original: PaymentScheduleRow[], index: number): PaymentScheduleRow[] => {
  const removedPercentage = original[index].percentage
  return original.filter((_, i) => i !== index)
  .map((x, i, array) => {
    return {
      ...x,
      sequence: i,
      percentage: i === array.length - 1
        ? x.percentage + removedPercentage
        : x.percentage
    }
  })
}

export const updatePaymentTitle = (original: PaymentScheduleRow[], index: number, value: string): PaymentScheduleRow[] => {
  return original.map((x, i) => {
    return i === index ? { ...x, title: value } : x
  })
}

export const updatePaymentNote = (original: PaymentScheduleRow[], index: number, value: string): PaymentScheduleRow[] => {
  return original.map((x, i) => {
    return i === index ? { ...x, note: value } : x
  })
}

export const updatePaymentPercentage = (original: PaymentScheduleRow[], index: number, value: string, minimumAllowedFinalPercent: number): PaymentScheduleRow[] => {
  const parsed = parsePaymentPercentage(value)
  if (isNaN(parsed) || parsed < 0) {
    return original
  }

  return updatePaymentScheduleWithNewPercentage(original, index, parsed, minimumAllowedFinalPercent)
}

const parsePaymentPercentage = (input: string): number => {
  const value = parseFloat(input) / 100
  const parsed = 0 <= value && value <= 1 ? value : NaN
  return roundToPrecision(parsed, 2)
}

export const updatePaymentScheduleWithNewPercentage = (original: PaymentScheduleRow[], index: number, value: number, minimumAllowedFinalPercent: number): PaymentScheduleRow[] => {
  let newPercent = value
  const originalPercent = original[index].percentage
  const originalFinalPercent = original[original.length - 1].percentage
  let newFinalPercent = roundToPrecision(originalFinalPercent - (value - originalPercent), 2)

  if (newFinalPercent < minimumAllowedFinalPercent) {
    newFinalPercent = minimumAllowedFinalPercent
    newPercent = roundToPrecision((originalFinalPercent + originalPercent) - minimumAllowedFinalPercent, 2)
  }

  return original.map((x, i) => {
    if (i === index) {
      return { ...x, percentage: newPercent }
    }
    if (i === original.length - 1) {
      return { ...x, percentage: newFinalPercent }
    }
    return x
  })
}

export const createDefaultPaymentScheduleOptions = (): PaymentScheduleOptions => ({
  quotedAndTimeAndMaterials: {
    selectedOption: 'progress-based',
    intervalBased: {
      totalPayments: 1,
      recurring: createDefaultRecurringPayment()
    },
    progressBased: []
  },
  perVisit: {
    recurring: createDefaultRecurringPayment()
  }
})

export const createDefaultRecurringPayment = (): RecurringPayment => ({
  estimatedStartDate: null,
  invoiceEvery: 1,
  invoiceEveryUnit: 'months',
  weeklyRepeatOn: 'Monday',
  monthlyOption: 'day',
  monthlyRepeatOnDay: 1,
  monthlyRepeatEveryNthWeek: 'first',
  monthlyRepeatOn: 'Monday',
  hideScheduleOnProposal: false
})

export type PaymentScheduleOptionsAction = 
| { type: 'selectQuotedAndTimeAndMaterialsPaymentOption', value: PaymentScheduleQuotedAndTimeAndMaterialsOption }
| { type: 'updateIntervalBasedTotalPayments', value: number }
| { type: 'updateIntervalBasedRecurringPayment', value: RecurringPayment }
| { type: 'updatePerVisitRecurringPayment', value: RecurringPayment }

export const paidUpfrontSchedule = (): PaymentScheduleRow[] => [{
  ...defaultPaymentScheduleRow(),
  title: 'Paid Upfront',
  percentage: 1,
  sequence: 0
}]

/**
 * Selecting an option only changes the selection. Overwriting progressBased here destroyed the
 * user's milestones, so going back to Progress-based showed the leftover 100% row and no way to
 * edit it (SUFT-241). preparePayloadForSave builds the row the api stores for paid-upfront.
 */
export const updateQuotedAndTimeAndMaterialsSelectedOption = (options: PaymentScheduleOptions, value: PaymentScheduleQuotedAndTimeAndMaterialsOption): PaymentScheduleOptions => {
  return {
    ...options,
    quotedAndTimeAndMaterials: {
      ...options.quotedAndTimeAndMaterials,
      selectedOption: value
    }
  }
}

export const updateIntervalBasedTotalPayments = (options: PaymentScheduleOptions, value: number): PaymentScheduleOptions => {
  return {
    ...options,
    quotedAndTimeAndMaterials: {
      ...options.quotedAndTimeAndMaterials,
      intervalBased: {
        ...options.quotedAndTimeAndMaterials.intervalBased,
        totalPayments: Math.max(1, Math.min(Math.round(value), 99))
      }
    }
  }
}

export const updateIntervalBasedRecurringPayment = (options: PaymentScheduleOptions, value: RecurringPayment): PaymentScheduleOptions => {
  return {
    ...options,
    quotedAndTimeAndMaterials: {
      ...options.quotedAndTimeAndMaterials,
      intervalBased: {
        ...options.quotedAndTimeAndMaterials.intervalBased,
        recurring: value
      }
    }
  }
}

export const updatePerVisitRecurringPayment = (options: PaymentScheduleOptions, value: RecurringPayment): PaymentScheduleOptions => {
  return {
    ...options,
    perVisit: {
      ...options.perVisit,
      recurring: value
    }
  }
}

export const updateRecurringEstimatedStartDate = (recurringPayment: RecurringPayment, value: string | null): RecurringPayment => {
  return {
    ...recurringPayment,
    estimatedStartDate: value
  }
}

export const updateRecurringInvoiceEvery = (recurringPayment: RecurringPayment, value: number): RecurringPayment => {
  return {
    ...recurringPayment,
    invoiceEvery: Math.max(1, value)
  }
}

export const updateRecurringInvoiceEveryUnit = (recurringPayment: RecurringPayment, value: InvoiceEveryUnit): RecurringPayment => {
  return {
    ...recurringPayment,
    invoiceEveryUnit: value
  }
}

export const updateRecurringWeeklyRepeatOn = (recurringPayment: RecurringPayment, value: DayOfWeek): RecurringPayment => {
  return {
    ...recurringPayment,
    weeklyRepeatOn: value
  }
}

export const updateRecurringMonthlyOption = (recurringPayment: RecurringPayment, value: RecurringPaymentMonthlyOption): RecurringPayment => {
  return {
    ...recurringPayment,
    monthlyOption: value
  }
}

export const updateRecurringMonthlyRepeatOnDay = (recurringPayment: RecurringPayment, value: number): RecurringPayment => {
  return {
    ...recurringPayment,
    monthlyRepeatOnDay: Math.max(1, Math.min(value, 28))
  }
}

export const updateRecurringMonthlyRepeatEveryNthWeek = (recurringPayment: RecurringPayment, value: NthWeek): RecurringPayment => {
  return {
    ...recurringPayment,
    monthlyRepeatEveryNthWeek: value
  }
}

export const updateRecurringMonthlyRepeatOn = (recurringPayment: RecurringPayment, value: DayOfWeek): RecurringPayment => {
  return {
    ...recurringPayment,
    monthlyRepeatOn: value
  }
}

export const updateRecurringHideScheduleOnProposal = (recurringPayment: RecurringPayment, value: boolean): RecurringPayment => {
  return {
    ...recurringPayment,
    hideScheduleOnProposal: value
  }
}

const nthWeekMap = {
  'first': 'First',
  'second': 'Second',
  'third': 'Third',
  'fourth': 'Fourth'
}

const getNthWeek = (value: NthWeek): string => nthWeekMap[value] ? nthWeekMap[value] : value

export const buildRecurringPaymentText = (recurringPayment: RecurringPayment): string => {
  if (recurringPayment.hideScheduleOnProposal) {
    return ''
  }

  if (recurringPayment.invoiceEveryUnit === 'weeks') {
    const weeks = recurringPayment.invoiceEvery === 1 ? 'Week' : `${recurringPayment.invoiceEvery} Weeks`
    return `Invoice Every ${weeks} on ${recurringPayment.weeklyRepeatOn}`
  }

  const months = recurringPayment.invoiceEvery === 1 ? 'Month' : `${recurringPayment.invoiceEvery} Months`
  const when = recurringPayment.monthlyOption === 'day'
    ? `Day ${recurringPayment.monthlyRepeatOnDay}`
    : `the ${getNthWeek(recurringPayment.monthlyRepeatEveryNthWeek)} ${recurringPayment.monthlyRepeatOn}`
  return `Invoice Every ${months} on ${when}`
}